/**
 * 저장된 도장 — PDF 편집기 두 곳(PdfEditor.jsx · tm/pages/PdfEditorPage.tsx)이 같이 쓰는 보관함 (2026-10-31)
 *  · 최대 30개(지도사님 「10개까지만 보이는데 30개까지」)
 *  · 브라우저 저장소(localStorage, 약 5MB)는 원본 사진 그대로면 10개 안팎에서 가득 차 조용히 저장이 실패했다
 *    → 보관용 사본은 긴 변 360px PNG 로 줄여 저장(투명 배경 유지) · 그래도 넘치면 오래된 것부터 빼고 다시 저장
 *  · 문서에 넣는 도장은 원본 그대로(보관 사본은 목록 · 다시 넣기용)
 */
export const SAVED_STAMPS_KEY = 'pdf_saved_stamps';
export const MAX_SAVED_STAMPS = 30;
const THUMB = 360;

export interface SavedStamp { id: string; data: string; ts?: number; name?: string }

export function getSavedStamps(): SavedStamp[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(SAVED_STAMPS_KEY) || '[]');
    return Array.isArray(v) ? (v as SavedStamp[]).filter((s) => s && typeof s.data === 'string') : [];
  } catch { return []; }
}

/** 저장 — 넘치면 오래된 것부터 빼며 다시 시도 · 실제로 남은 목록을 돌려준다 */
function writeStamps(list: SavedStamp[]): SavedStamp[] {
  let cur = list.slice(0, MAX_SAVED_STAMPS);
  while (cur.length) {
    try { localStorage.setItem(SAVED_STAMPS_KEY, JSON.stringify(cur)); return cur; } catch { cur = cur.slice(0, -1); }
  }
  try { localStorage.removeItem(SAVED_STAMPS_KEY); } catch { /* 저장소 막힘 */ }
  return [];
}

/** 보관용으로 줄이기 — 긴 변 THUMB px 넘으면 줄이고 PNG 로 · 실패하면 원본 */
export function shrinkStamp(src: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const k = Math.min(1, THUMB / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
      const w = Math.max(1, Math.round((img.naturalWidth || 1) * k));
      const h = Math.max(1, Math.round((img.naturalHeight || 1) * k));
      const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      const ctx = cv.getContext('2d');
      if (!ctx) { resolve(src); return; }
      ctx.drawImage(img, 0, 0, w, h);
      try { const out = cv.toDataURL('image/png'); resolve(out.length < src.length ? out : src); } catch { resolve(src); }
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });
}

/** 새 도장 보관 — 같은 그림이면 맨 앞으로만 */
export async function saveStamp(src: string, name?: string): Promise<SavedStamp[]> {
  const data = await shrinkStamp(src);
  const list = getSavedStamps();
  const hit = list.find((s) => s.data === data);
  const rest = list.filter((s) => s.data !== data);
  return writeStamps([hit || { id: Date.now().toString(36), data, ts: Date.now(), name: name || '' }, ...rest]);
}

export function renameStamp(id: string, name: string): SavedStamp[] {
  return writeStamps(getSavedStamps().map((s) => (s.id === id ? { ...s, name } : s)));
}

export function deleteStamp(id: string): SavedStamp[] {
  return writeStamps(getSavedStamps().filter((s) => s.id !== id));
}
